import {ApiClient, ApiClientKeys, ApiClientPatchTypes, ApiClientResult} from "app/Interface/Api/Client/ApiClient"
import {ApiDefaultSettingResult} from "app/Interface/Api/Client/ApiDefaultSetting"
import {ApiInvoiceNumberSettingResult} from "app/Interface/Api/Client/ApiInvoiceNumberSetting"
import {ApiAddressResult} from "app/Interface/Api/Module/ApiAddress"
import {ApiBankAccountResult} from "app/Interface/Api/Module/ApiBankAccount"
import {ApiBusinessNumbersResult} from "app/Interface/Api/Module/ApiBusinessNumbers"
import {ApiEmailAddressResult} from "app/Interface/Api/Module/ApiEmailAddress"
import {DefaultDataKey} from "const/DefaultDataKey"
import {Module} from "const/Module"
import Logger from "logger/logger"
import {useEffect, useState} from "react"
import {Subscription} from "rxjs"
import API from "api/API"
import {rootClientQuery, rootClientStore} from "store/client/client.store"
import {objectCleanValues} from "utils/ObjectCompare"
import {onEmit} from "utils/onEmit"

export const SubscribeClient = {
  edit: 'edit',
  update: "update",

  clientId: 'clientId',
  clientName: 'clientName',
  clientModules: 'clientModules',
  address: 'address',
  emailAddress: 'emailAddress',
  businessNumbers: "businessNumbers",
  bankAccount: "bankAccount",

  defaultSettings: 'defaultSettings',
  invoiceNumberSettings: 'invoiceNumberSettings',
  invoicePrintTemplate: 'invoicePrintTemplate',
}

function ClientService(subscribe: string[], caller: string) {

  const subscribed: Subscription[] = []

  const [isLoading, setIsLoading] = useState<boolean>()

  const [edit, setEdit] = useState<ApiClient>()
  const [update, setUpdate] = useState<string[]>([])

  const [clientId, setClientId] = useState<string>()

  const [clientName, setClientName] = useState<string>()
  const [clientEditName, setClientEditName] = useState<string>()

  const [address, setAddress] = useState<ApiAddressResult>()
  const [editAddress, setEditAddress] = useState<ApiAddressResult>()

  const [emailAddress, setEmailAddress] = useState<ApiEmailAddressResult>()
  const [editEmailAddress, setEditEmailAddress] = useState<ApiEmailAddressResult>()

  const [bankAccount, setBankAccount] = useState<ApiBankAccountResult>()
  const [editBankAccounts, setEditBankAccount] = useState<ApiBankAccountResult>()

  const [businessNumbers, setBusinessNumbers] = useState<ApiBusinessNumbersResult>()
  const [editBusinessNumbers, setEditBusinessNumbers] = useState<ApiBusinessNumbersResult>()

  const [defaultSettings, setDefaultSettings] = useState<ApiDefaultSettingResult[]>([])
  const [editDefaultSettings, setEditDefaultSettings] = useState<ApiDefaultSettingResult[]>()

  const [invoiceNumberSettings, setInvoiceNumberSettings] = useState<ApiInvoiceNumberSettingResult>()
  const [editInvoiceNumberSettings, setEditInvoiceNumberSettings] = useState<ApiInvoiceNumberSettingResult>()

  const [modules, setModules] = useState<string[]>([])
  const [invoicePrintTemplate, setInvoicePrintTemplate] = useState<string>()

  useEffect(() => {
    if (!subscribe) throw Error(`Service.Client: Define what you want to subscribe to`)
    if (!caller) throw Error("Service.Client: Define where you are calling from")

    if (undefined === rootClientQuery.getValue().loading) {
      setIsLoading(true)
      rootClientStore.setLoading(true)
      API.get<ApiClientResult>(`/client`).then(clientData => {
        rootClientStore.update({
          loading: false,
          ...clientData.data
        })
        setIsLoading(false)
      })
    }

    Logger(`Service.Client.init.${caller}`, subscribe.join(', '))

    subscribed.push(onEmit(rootClientQuery.select('loading'), newLoading => {
      setIsLoading(newLoading)
    }))

    if (subscribe.includes(SubscribeClient.clientId))
      subId()

    if (subscribe.includes(SubscribeClient.clientName))
      subClientName()

    if (subscribe.includes(SubscribeClient.address))
      subAddress()

    if (subscribe.includes(SubscribeClient.emailAddress))
      subEmail()

    if (subscribe.includes(SubscribeClient.businessNumbers))
      subBusinessNumbers()

    if (subscribe.includes(SubscribeClient.bankAccount))
      subBankAccount()

    if (subscribe.includes(SubscribeClient.edit))
      subEdit()

    if (subscribe.includes(SubscribeClient.update))
      subUpdate()

    if (subscribe.includes(SubscribeClient.clientModules))
      subModules()

    if (subscribe.includes(SubscribeClient.defaultSettings))
      subDefaultSettings()

    if (subscribe.includes(SubscribeClient.invoiceNumberSettings))
      subInvoiceNumberSettings()

    if (subscribe.includes(SubscribeClient.invoicePrintTemplate))
      subInvoicePrintTemplate()

    return () => {
      Logger(`Service.Client.unloading.${caller}`)
      subscribed.map(s => s.unsubscribe())
    }
  }, [])

  const setEditData = (key: ApiClientKeys, value: ApiClientPatchTypes) => {
    Logger('ClientService.updateData.key', key, value)
    const storeEdit = rootClientQuery.getValue().edit
    if (undefined === value && undefined === storeEdit)
      return

    rootClientStore.update(state => {
      if (undefined === state.edit)
        state.edit = {}

      if (Object.keys(state.edit).length === 1 && undefined === value)
        state.edit = undefined
      else
        state.edit[key] = value
    })
  }

  const clearClientEdit = () => {
    rootClientStore.update(state => {
      state.edit = undefined
    })
  }

  const clearClientUpdateKey = (mercureKey: string, apiKey: ApiClientKeys) => {
    const u = rootClientQuery.getValue().update
    Logger(`Service.Client.${caller}`, 'clearClientUpdateKey', mercureKey, apiKey, u)
    if (undefined === u || !u.includes(mercureKey))
      return

    const edit = rootClientQuery.getValue().edit

    // Remove update key
    rootClientStore.update(state => {
      const nUpdate = state.update?.filter(k => k !== mercureKey)
      state.update = nUpdate && nUpdate.length > 0 ? nUpdate : undefined
    })

    // Only when we are the one that did the edit, we need to clear the edit key.
    if (undefined !== edit && undefined !== edit[apiKey]) {
      rootClientStore.update(state => {
        // Remove edit key
        if (state.edit) {
          const edit = objectCleanValues(state.edit)
          const editL = Object.keys(edit).length
          if (editL === 1)
            state.edit = undefined
          else
            state.edit[apiKey] = undefined
        }
      })
    }
  }

  const saveClientEdit = () => {
    const editData = rootClientStore.getValue().edit
    if (undefined === editData)
      return

    if (editData.name)
      API.patch(`/${Module.client}/name`, {name: editData.name}).then()

    if (editData.address)
      API.patch(`/${Module.client}/address`, {...editData.address}).then()

    if (editData.emailAddress)
      API.patch(`/${Module.client}/emailAddress`, {...editData.emailAddress}).then()

    if (editData.bankAccounts)
      API.patch(`/${Module.client}/bankAccount`, {...editData.bankAccounts}).then()

    if (editData.businessNumbers)
      API.patch(`/${Module.client}/businessNumbers`, {...editData.businessNumbers}).then()

    if (editData.settingsDefaults)
      API.patch(`/${Module.client}/defaultSettings`, editData.settingsDefaults).then()

    // if (editData.invoiceNumberSetting)
    //   API.patch(`/${Module.client}/${Module.invoiceNumberSettings}`, editData.invoiceNumberSetting).then()
  }

  const getDefaultSetting = (dataKey: string): string | undefined => {
    if (!Object.keys(DefaultDataKey).includes(dataKey))
      throw new Error(`Unknown key: '${dataKey}'`)

    return rootClientStore.getValue().settingsDefaults.find(d => d.dataKey === dataKey)?.value
  }

  const hasModule = (id: string): boolean => {
    return !!rootClientStore.getValue().clientModules.find(mId => mId === id)
  }

  return {
    clientIsLoading: undefined === isLoading || isLoading,
    setEditData,
    saveClientEdit,
    clearClientEdit,
    clearClientUpdateKey,
    getDefaultSetting,

    clientEdit: edit,
    clientUpdate: update,
    clientId,

    clientName,
    clientEditName,

    clientAddress: address,
    clientEditAddress: editAddress,

    clientEmailAddress: emailAddress,
    clientEditEmailAddress: editEmailAddress,

    clientBusinessNumbers: businessNumbers,
    clientEditBusinessNumbers: editBusinessNumbers,

    clientBankAccount: bankAccount,
    clientEditBankAccount: editBankAccounts,

    clientDefaultSettings: defaultSettings,
    clientEditDefaultSettings: editDefaultSettings,

    invoiceNumberSettings,
    editInvoiceNumberSettings,

    clientModules: modules,
    invoicePrintTemplate,

    hasModule,
  }

  function subId() {
    subscribed.push(onEmit(rootClientQuery.select('id'), newId => {
      setClientId(newId)
    }))
  }

  function subClientName() {
    subscribed.push(onEmit(rootClientQuery.select('name'), newName => {
      setClientName(newName)
    }))
    subscribed.push(onEmit(rootClientQuery.select('edit'), newEdit => {
      if (newEdit && newEdit.name)
        setClientEditName(newEdit.name)
      else
        setClientEditName(undefined)
    }))
  }

  function subAddress() {
    subscribed.push(onEmit(rootClientQuery.select('address'), newAddress => {
      setAddress(newAddress[0])
    }))
    subscribed.push(onEmit(rootClientQuery.select('edit'), newEdit => {
      if (newEdit && newEdit.address)
        setEditAddress(newEdit.address)
      else
        setEditAddress(undefined)
    }))
  }

  function subEmail() {
    subscribed.push(
      onEmit(rootClientQuery.select('emailAddress'), newEmailAddress => {
        Logger('ClientService.subEmail', newEmailAddress)
        setEmailAddress(newEmailAddress[0])
      }),
      onEmit(rootClientQuery.select('edit'), newEdit => {
        if (newEdit && newEdit.emailAddress)
          setEditEmailAddress(newEdit.emailAddress)
        else
          setEditEmailAddress(undefined)
      }))
  }

  function subBankAccount() {
    subscribed.push(
      onEmit(rootClientQuery.select('bankAccounts'), newBankAccount => {
        setBankAccount(newBankAccount[0])
      }),
      onEmit(rootClientQuery.select('edit'), newEdit => {
        if (newEdit && newEdit.bankAccounts)
          setEditBankAccount(newEdit.bankAccounts)
        else
          setEditBankAccount(undefined)
      }))
  }

  function subBusinessNumbers() {
    subscribed.push(
      onEmit(rootClientQuery.select('businessNumbers'), newBusinessNumbers => {
        setBusinessNumbers(newBusinessNumbers ?? undefined)
      }),
      onEmit(rootClientQuery.select('edit'), newEdit => {
        if (newEdit && newEdit.businessNumbers)
          setEditBusinessNumbers(newEdit.businessNumbers)
        else
          setEditBusinessNumbers(undefined)
      }))
  }

  function subEdit() {
    subscribed.push(onEmit(rootClientQuery.select('edit'), newEdit => {
      setEdit(newEdit)
    }))
  }

  function subUpdate() {
    subscribed.push(onEmit(rootClientQuery.select('update'), newUpdate => {
      setUpdate(newUpdate ?? [])
    }))
  }

  function subModules() {
    subscribed.push(onEmit(rootClientQuery.select('clientModules'), newModules => {
      setModules(newModules)
    }))
  }

  function subDefaultSettings() {
    subscribed.push(
      onEmit(rootClientQuery.select('settingsDefaults'), newSettingsDefaults => {
        setDefaultSettings(newSettingsDefaults)
      }),
      onEmit(rootClientQuery.select('edit'), newEdit => {
        if (newEdit && newEdit.settingsDefaults)
          setEditDefaultSettings(newEdit.settingsDefaults)
        else
          setEditDefaultSettings(undefined)
      })
    )
  }

  function subInvoiceNumberSettings() {
    subscribed.push(
      onEmit(rootClientQuery.select('invoiceNumberSetting'), newInvoiceNumberSettings => {
        setInvoiceNumberSettings(newInvoiceNumberSettings ?? undefined)
      }),
      onEmit(rootClientQuery.select('edit'), newEdit => {
        if (newEdit && newEdit.invoiceNumberSetting)
          setEditInvoiceNumberSettings(newEdit.invoiceNumberSetting)
        else
          setEditInvoiceNumberSettings(undefined)
      })
    )
  }

  function subInvoicePrintTemplate() {
    subscribed.push(onEmit(rootClientQuery.select('defaultInvoicePrintTemplate'), newPrintTemplate => {
      setInvoicePrintTemplate(newPrintTemplate ?? undefined)
    }))
  }

}


export default ClientService
